import "./FMRightPanel.scss";

import { serviceToInvoiceableEntity } from "components/Invoicing/invoiceAdapter";
import InvoiceContainer from "components/Invoicing/InvoiceContainer";
import Notes from "components/Notes";
import { serviceToNotesEntity } from "components/Notes/notesAdapter";
import {
  isCmsSubmissionSection,
  SECTION,
  serviceTypeHasCmsSubmission,
} from "constants/SettingsConstants";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  MSADataType,
} from "types/DataTypes";

import AmendedReviewSection from "../amended-review-screening/sections/AmendedReviewSection";
import ClientAnalysisSection from "../amended-review-screening/sections/ClientAnalysisSection";
import { ARS_SECTIONS, MCP_SECTIONS, MSA_SECTIONS } from "../constants";
import InvoiceCMS from "../invoice-cms/InvoiceCMS";
import FutureMedicalAuditHistory from "./FutureMedicalAuditHistory";
import {
  LegalReview,
  MedicalReview,
} from "./allocation-pages/AllocationReview";
import ClientSubmission from "./client-submission/ClientSubmission";
import CMSStatus from "./cms-status/CMSStatus";
import CMSSubmission from "./cms-submission/FMCMSSubmission";
import FinalSettlementDocuments from "./FMFinalSettlementDocuments";
import FMOperations from "./FMOperations";
import FMQuery from "./FMQuery";
import RatedAgeForm from "./rated-age/FMRatedAge";

/* this page is the right 2/3rds of the future medical section */
type FutureMedicalRightPanelProps = {
  section: string;
  service: MSADataType | CostProjectionDataType | AmendedReviewScreeningType;
  onSave: (field: string | null, value: unknown) => void;
};

// msa sections (MSA, MSA-111, Amended Review)
const msaSections = {
  "client-delivery": ({ service, onSave }) => (
    <ClientSubmission service={service} onSave={onSave} />
  ),
  "cms-status": ({ service }) => <CMSStatus msa={service as MSADataType} />,
  invoice: ({ service }) => {
    const entity = serviceToInvoiceableEntity(service);
    return entity ? (
      <InvoiceContainer
        entity={entity}
        invoiceType="future_medical"
        config={{ showHistory: true, showCMS: true, filterCMSInvoices: false }}
      />
    ) : null;
  },
  "invoice-cms": ({ service }) => <InvoiceCMS service={service} />,
  "cms-submission": ({ service }) => (
    <CMSSubmission msa={service as MSADataType} />
  ),
  fsd: ({ service, onSave }) => (
    <FinalSettlementDocuments service={service} onSave={onSave} />
  ),
} as const;

// cost projection sections (MCP, LCP)
const costProjectionSections = {
  "mcp-to-client": ({ service, onSave }) => (
    <ClientSubmission service={service} onSave={onSave} />
  ),
  "lcp-to-client": ({ service, onSave }) => (
    <ClientSubmission service={service} onSave={onSave} />
  ),
  invoice: ({ service }) => {
    const entity = serviceToInvoiceableEntity(service);
    return entity ? (
      <InvoiceContainer
        entity={entity}
        invoiceType="future_medical"
        config={{ showHistory: true, showCMS: false, filterCMSInvoices: true }}
      />
    ) : null;
  },
  "to-client": () => <div>Cost Projection to Client Section</div>,
} as const;

const amendedReviewSections = {
  "amended-review": AmendedReviewSection,
  analysis: ClientAnalysisSection,
  invoice: ({ service, onSave }) => {
    const entity = serviceToInvoiceableEntity(service);
    if (!entity) return null;

    return (
      <InvoiceContainer
        entity={entity}
        config={{
          no_invoice_required: service.no_invoice_required,
          onNoInvoiceRequiredChange: (value) =>
            onSave("no_invoice_required", value),
          showHistory: true,
          showCMS: true,
          filterCMSInvoices: false,
        }}
        invoiceType="future_medical"
      />
    );
  },
} as const;

// NTMSA / MSA-111 / Pre-MSA Snapshot are never submitted to CMS, so the CMS Submission,
// Invoice (CMS), CMS Status and F.S.D sections don't exist for them. Both the
// render map and the label config are derived through the same predicate so
// they can't drift apart.
const omitCmsSubmissionSections = <T extends Record<string, unknown>>(
  sections: T,
): Partial<T> =>
  Object.fromEntries(
    Object.entries(sections).filter(
      ([section]) => !isCmsSubmissionSection(section),
    ),
  ) as Partial<T>;

const msaSectionsWithoutCms = omitCmsSubmissionSections(msaSections);
const MSA_SECTIONS_WITHOUT_CMS = omitCmsSubmissionSections(MSA_SECTIONS);

const SERVICE_SECTIONS = {
  [SECTION.MSA]: msaSections,
  [SECTION.MSA_111]: msaSectionsWithoutCms,
  [SECTION.NTMSA]: msaSectionsWithoutCms,
  [SECTION.AMENDED_REVIEW]: msaSections,
  [SECTION.PRE_MSA]: msaSectionsWithoutCms,
  [SECTION.MCP]: costProjectionSections,
  [SECTION.LCP]: costProjectionSections,
  [SECTION.AMENDED_REVIEW_SCREENING]: amendedReviewSections,
} as const;

// common sections shared across all services
const COMMON_SECTIONS = {
  operations: ({ service }) => <FMOperations service={service} />,
  query: FMQuery,
  "rated-age": RatedAgeForm,
  "medical-review": MedicalReview,
  "legal-review": LegalReview,
  "audit-history": () => <FutureMedicalAuditHistory />,
  notes: ({ service }) => {
    const entity = serviceToNotesEntity(service);
    return entity ? <Notes entity={entity} /> : null;
  },
} as const;

// the MSA label config, with the CMS sections dropped for the types in
// MSA_TYPES_WITHOUT_CMS_SUBMISSION (same predicate as SERVICE_SECTIONS above,
// the left nav and the status bar)
const msaSectionConfigFor = (serviceType: string) =>
  serviceTypeHasCmsSubmission(serviceType)
    ? MSA_SECTIONS
    : MSA_SECTIONS_WITHOUT_CMS;

const getSectionConfig = (serviceType: string) => {
  switch (serviceType) {
    case SECTION.MSA:
    case SECTION.MSA_111:
    case SECTION.NTMSA:
    case SECTION.AMENDED_REVIEW:
    case SECTION.PRE_MSA:
      return msaSectionConfigFor(serviceType);
    case SECTION.MCP:
    case SECTION.LCP:
      return MCP_SECTIONS;
    case SECTION.AMENDED_REVIEW_SCREENING:
      return ARS_SECTIONS;
    default:
      // unrecognised service types fall back to the MSA labels (only the
      // COMMON_SECTIONS actually render for them, since SERVICE_SECTIONS has
      // no entry). Routed through the same predicate so the fallback can never
      // hand out CMS section labels to a type that is in the no-CMS list.
      return msaSectionConfigFor(serviceType);
  }
};

const FutureMedicalRightPanel = ({
  section,
  service,
  onSave,
}: FutureMedicalRightPanelProps) => {
  const SECTIONS = getSectionConfig(service.type);
  const sectionLabel = SECTIONS[section]?.label;

  // render the section based on the service type
  const ServiceSection = SERVICE_SECTIONS[service.type]?.[section];
  const SharedSection = COMMON_SECTIONS[section];

  // only pass coverage_complete if it exists on the service (not on ARS)
  const isCompletable = "coverage_complete" in service;

  return (
    <div className="right-panel">
      <div className="swim-headers">{sectionLabel}</div>
      {ServiceSection && <ServiceSection service={service} onSave={onSave} />}
      {SharedSection && (
        <SharedSection
          service={service}
          onSave={onSave}
          serviceType={service.type}
          isComplete={isCompletable ? service.coverage_complete : undefined}
        />
      )}
    </div>
  );
};

export default FutureMedicalRightPanel;
